import { useLocation, useNavigate } from 'react-router-dom';
import { useGoBack } from '@/lib/goBack';
import { useKeptScroll } from '@/lib/keptScroll';
import { useAuth } from '@/lib/auth';
import { useChats } from '@/data/store';
import { BackIcon } from '@/components/Icons';
import { useOverlay } from '@/components/Overlay';
import { ReminderSheet } from '@/components/ReminderSheet';
import { shortDate, todayIso, TYPE_WORDS, useReminders, type Reminder } from '@/lib/reminders';

// Every follow-up reminder this person can see, soonest first and grouped by month (Gary, 3 Nov 2026), reached from
// the row at the top of the chat list, as Archived is. Due and overdue ones come first. Tap one to open its chat.

function monthTitle(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-GB', { month: 'long', ...(sameYear ? {} : { year: 'numeric' }) });
}

function groups(list: Reminder[], today: string): { title: string; items: Reminder[] }[] {
  const out: { title: string; items: Reminder[] }[] = [];
  const due = list.filter((r) => r.date <= today);
  if (due.length) out.push({ title: 'Due now', items: due });
  for (const r of list.filter((x) => x.date > today)) {
    const title = monthTitle(r.date);
    const last = out[out.length - 1];
    if (last && last.title === title) last.items.push(r);
    else out.push({ title, items: [r] });
  }
  return out;
}

export function RemindersScreen() {
  const listScroll = useKeptScroll<HTMLElement>('reminders');
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { pathname } = useLocation();
  const { profile } = useAuth();
  const { live } = useChats();
  const { openSheet, closeSheet, toast } = useOverlay();
  const { reminders, loaded } = useReminders(live);
  const today = todayIso();
  const back = () => { if (pathname === '/reminders') goBack('/'); else navigate('/'); };

  const open = (r: Reminder) => {
    if (r.chatId) { navigate(`/chat/${r.chatId}`); return; }
    if (!profile?.id) return;
    openSheet(<ReminderSheet chat={{ id: '', customerId: r.customerId ?? undefined, leadId: r.leadId ?? undefined, name: r.name }} reminder={r}
      userId={profile.id} onDone={(m) => { closeSheet(); toast(m); }} />);
  };

  return (
    <>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn" aria-label="Back" onClick={back}><BackIcon /></button>
        <h1 className="chats-topbar-title">Reminders</h1>
      </header>
      <main ref={listScroll} className="chats-screen chats-screen--grey">
        <p className="chats-info">Follow-ups set by Leo or the team. On the day, the chat comes back to its owner with an alert. Add one from a chat's Info.</p>
        {loaded && reminders.length === 0 && <p className="chats-empty">No reminders.</p>}
        {groups(reminders, today).map((g) => (
          <div key={g.title}>
            <p className="chats-group-title">{g.title}</p>
            <div className="chats-group">
              {g.items.map((r) => (
                <button key={r.id} type="button" className="chats-set" onClick={() => open(r)}>
                  <div className="chats-set-main">
                    {r.name}
                    <small>
                      {shortDate(r.date)} · {r.note ?? TYPE_WORDS[r.type]}
                      {r.area ? ` · ${r.area}` : ''}
                      {` · ${r.setBy ? `Set by ${r.setBy}` : 'Set by Leo'}`}
                    </small>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </main>
    </>
  );
}
